import React, { CSSProperties } from 'react'
import styled from '@emotion/styled'
import { ValidationIssue } from 'api'
import { getIssuesForField, Severity } from 'model'
import { ValidationTooltip } from 'view/components/tooltip'
import * as constants from 'view/styles/constants'
import { isNotEmpty } from 'utils/predicate'

export const PendingContainer = styled.div`
  margin-bottom: 20px;

  h4 {
    margin-bottom: 10px;
  }

  button {
    margin-left: auto;
  }
`

export const PendingScrollContainer = styled.div`
  max-height: 400px;
  overflow-y: auto;
  padding-right: 5px;
  margin-bottom: 20px;
`

export const PendingTable = styled.table`
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0 2px;

  thead {
    th {
      text-align: left;
      position: sticky;
      top: -1px;
      z-index: 2;
      background: ${constants.CORE_PRIMARY};
      font-weight: normal;

      font-size: 13px;
      padding: 5px;

      :first-child {
        padding-left: 15px;
      }
    }
  }
`

export enum ValidationStatus {
  DEFAULT,
  WARN,
  ERROR
}

const CellValidationStatusColourMap = {
  [ValidationStatus.DEFAULT]: 'initial',
  [ValidationStatus.ERROR]: constants.ACCENT_BAD,
  [ValidationStatus.WARN]: constants.ACCENT_BE_AWARE
}

const RowValidationStatusColourMap = {
  ...CellValidationStatusColourMap,
  [ValidationStatus.DEFAULT]: constants.ACCENT_PRIMARY
}

export const PendingCell = styled.td<{
  cellValidationStatus?: ValidationStatus
  isError?: boolean
  rowValidationStatus?: ValidationStatus
}>`
  border-bottom: 2px solid ${props => CellValidationStatusColourMap[props.cellValidationStatus ?? ValidationStatus.DEFAULT]} !important;
  border: 1px solid ${constants.CORE_PRIMARY_2};
  border-bottom: 1px solid ${constants.CORE_PRIMARY_2};
  white-space: no-wrap;
  padding: 7px 5px;
  color: ${constants.CORE_PRIMARY_6};

  :first-child {
    padding-left: 10px;
    font-weight: bold;
    border-left: 5px solid ${props => RowValidationStatusColourMap[props.rowValidationStatus ?? ValidationStatus.DEFAULT]};
  }

  :last-child {
    border-right: 1px solid ${constants.CORE_PRIMARY_2};
  }
`

type PendingValidationCellProps = {
  validationIssues: ValidationIssue[]
  fieldName: string
  rowValidationStatus?: ValidationStatus
  dataTestId?: string
  style?: CSSProperties
  children?: React.ReactNode
}

export const PendingValidationCell: React.FunctionComponent<PendingValidationCellProps> = props => {
  const { children, dataTestId, fieldName, rowValidationStatus = ValidationStatus.DEFAULT, style, validationIssues } = props
  const fieldValidationIssues = getIssuesForField(validationIssues, fieldName)
  const hasErrors = isNotEmpty(fieldValidationIssues.filter(i => i.severity === Severity.ERROR))
  const hasWarnings = isNotEmpty(fieldValidationIssues.filter(i => i.severity === Severity.WARN))
  const cellValidationStatus = hasErrors ? ValidationStatus.ERROR : hasWarnings ? ValidationStatus.WARN : ValidationStatus.DEFAULT

  return (
    <ValidationTooltip validationIssues={fieldValidationIssues}>
      <PendingCell
        rowValidationStatus={rowValidationStatus}
        isError={fieldValidationIssues.length > 0}
        cellValidationStatus={cellValidationStatus}
        data-test-id={dataTestId}
        style={style}>
        {children}
      </PendingCell>
    </ValidationTooltip>
  )
}
